"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";
import { formatEventSessionDisplay } from "@fitro/date-time/display";
import type { PublicScheduleEvent } from "../../../services/public-schedule.service";
import { useTranslation } from "../../../hooks/useTranslation";
import { formatPriceMinor } from "../../../lib/formatPriceMinor";
import { LowestPrice30dNote } from "../promotions/PriceNotes";
import { publicEventPrice } from "./publicEventPrice";
import { getPublicEventDetailsHref } from "./publicEventLinks";
import {
  PublicPricingTable,
  PricingConditions,
  pricingCell,
  pricingName,
  pricingAmount,
  pricingBody,
} from "./PublicPricingTable";

/** The same entry quote and details destination used by the public event schedule. */
export function PublicEventPricing({
  events,
}: {
  events: PublicScheduleEvent[];
}) {
  const { t, locale, dateTime } = useTranslation("landing");
  const m = useTranslations("landing.publicEntry.pricing.compact");
  const sorted = [...events].sort(
    (a, b) =>
      Date.parse(a.startDateTime) - Date.parse(b.startDateTime) ||
      Date.parse(a.endDateTime) - Date.parse(b.endDateTime) ||
      a.id.localeCompare(b.id),
  );
  return (
    <PublicPricingTable
      eventDates
      label={t("publicEntry.events.title")}
      headers={[m("singleEntryColumn"), m("eventColumn"), m("datesColumn")]}
    >
      {sorted.map((event) => {
        const entry = publicEventPrice(event);
        const money = (amount: number) =>
          formatPriceMinor(
            amount,
            entry?.kind === "paid"
              ? entry.currency
              : (event.priceCurrency ?? "PLN"),
            locale,
          );
        const name =
          event.className?.trim() || t("publicEntry.events.fallbackTitle");
        const href = getPublicEventDetailsHref(event);
        const session = formatEventSessionDisplay(
          dateTime,
          event.startDateTime,
          event.endDateTime,
        );
        return (
          <tbody key={event.id} className={pricingBody}>
            <tr>
              <td className={pricingCell}>
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  {entry?.kind === "paid" && entry.lowestPrice30dMinor !== null ? (
                    <s className="self-end whitespace-nowrap text-sm text-slate-500">{money(entry.lowestPrice30dMinor)}</s>
                  ) : null}
                <span className={pricingAmount}>
                    {entry?.kind === "paid"
                      ? money(entry.amountMinor)
                      : entry?.kind === "free"
                        ? t("publicEntry.pricing.classes.free")
                        : "—"}
                  </span>
                </div>
                {entry?.kind === "paid" && entry.lowestPrice30dMinor !== null ? (
                  <>
                    <PricingConditions>
                      <LowestPrice30dNote
                        price={money(entry.lowestPrice30dMinor)}
                        className="text-xs font-normal leading-5 text-slate-600"
                      />
                    </PricingConditions>
                  </>
                ) : null}
              </td>
              <th
                scope="row"
                className={`${pricingCell} ${pricingName} [overflow-wrap:anywhere]`}
              >
                {href ? (
                  <Link
                    href={href}
                    className="underline-offset-4 hover:underline"
                  >
                    {name}
                  </Link>
                ) : (
                  name
                )}
              </th>
              <td className={`${pricingCell} text-right lg:whitespace-nowrap`}>
                <span className="block lg:inline lg:whitespace-nowrap">{session.primaryLabel}</span>{" "}
                <span className="block lg:inline lg:whitespace-nowrap">
                  {session.secondaryPrefix}
                  {session.secondaryLabel}
                </span>
              </td>
            </tr>
          </tbody>
        );
      })}
    </PublicPricingTable>
  );
}
